import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faExclamationTriangle, faCheckCircle, faChurch } from '@fortawesome/free-solid-svg-icons'; // Removed faSync

// Import Firebase services needed directly (adjust path if needed)
import { auth, functions } from '../firebase';      // Core Firebase services
// Removed db, doc, getDoc
import { httpsCallable } from 'firebase/functions';     // Functions callable utility
import { signInWithEmailAndPassword, sendEmailVerification } from "firebase/auth"; // Auth functions

// Define the callable function reference outside the component
const newUserCallable = httpsCallable(functions, 'newUser');

function CreateAccountPage() {
    // State for form fields
    const [email, setEmail] = useState('');
    const [fullName, setFullName] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    // State for operation status
    const [orgIdFromUrl, setOrgIdFromUrl] = useState(null); // Keep track of the ID from URL
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null); // Error state
    const [successMessage, setSuccessMessage] = useState(null); // Success message state

    // Removed churchName and isChurchNameLoading state

    // React Router hooks
    const { orgId } = useParams(); // orgId will be string | undefined
    const navigate = useNavigate();

    // Removed fetchChurchName function

    // Effect to handle orgId from URL
    useEffect(() => {
        if (orgId) {
            console.log("Organization ID detected from URL:", orgId);
            setOrgIdFromUrl(orgId);
        } else {
            console.log("No Organization ID provided in URL.");
            setOrgIdFromUrl(null);
        }
    }, [orgId]);

    // Form validation function (Unchanged)
    const validateForm = () => {
        let isValid = true;
        let validationError = null;
        if (!email.trim() || !fullName.trim() || !password.trim() || !confirmPassword.trim()) {
            validationError = "All fields (Email, Full Name, Password, Confirm Password) are required."; isValid = false;
        } else if (!/\S+@\S+\.\S+/.test(email)) {
             validationError = "Please enter a valid email address."; isValid = false;
        } else if (password.length < 6) {
            validationError = "Password must be at least 6 characters long."; isValid = false;
        } else if (password !== confirmPassword) {
            validationError = "Passwords do not match."; isValid = false;
        }
        setError(validationError);
        return isValid;
    };

    // Form submission handler
    const handleSubmit = async (event) => { // Removed type for event in JS
        event.preventDefault();

        if (isLoading || successMessage) {
            return;
        }
        if (!validateForm()) {
            return;
        }

        setError(null);
        setIsLoading(true);
        setSuccessMessage(null);

        const currentPassword = password;

        const payload = {
            email: email.trim(),
            fullName: fullName.trim(),
            password: currentPassword,
            orgId: orgIdFromUrl
        };

        console.log("Calling 'newUser' function with payload:", { ...payload, password: '***' });

        try {
            const result = await newUserCallable(payload);
            // *** FIX: Removed TypeScript 'as BaseResponse' assertion ***
            const resultData = result.data;
            console.log("'newUser' function result:", resultData);

            // Use optional chaining which works fine in JS
            if (resultData?.success) {
                const createdEmail = email.trim();

                console.log(`Backend success. Attempting client-side sign in for ${createdEmail}...`);
                signInWithEmailAndPassword(auth, createdEmail, currentPassword)
                    .then((userCredential) => {
                        const user = userCredential.user;
                        console.log(`Client-side sign in successful for user ${user.uid}. Sending verification email...`);
                        sendEmailVerification(user)
                            .then(() => {
                                console.log("Verification email sent successfully.");
                                const successMsg = orgIdFromUrl
                                    ? "Your account has been created! Please check your email to verify your address. Redirecting..."
                                    : "Your account has been created! Please check your email to verify your address. Redirecting..."
                                setSuccessMessage(successMsg);
                                setEmail(''); setFullName(''); setPassword(''); setConfirmPassword('');
                                setTimeout(() => { navigate('/dashboard'); }, 3500);
                            })
                            .catch((emailError) => {
                                console.error("Error sending verification email:", emailError);
                                setError(`Account created and signed in, but failed to send verification email: ${emailError.message}. Please try logging in and requesting verification again later.`);
                                setIsLoading(false);
                            });
                    })
                    .catch((signInError) => {
                        console.error("Error signing in new user after creation:", signInError);
                        setError(`Account created on backend, but failed to sign you in automatically: ${signInError.message}. Please try logging in manually.`);
                        setIsLoading(false);
                    });

            } else {
                 const backendMessage = resultData?.message || "Failed to create account. Please try again.";
                 const specificError = backendMessage.includes('already exists')
                   ? "An account with this email already exists. Try logging in instead."
                   : backendMessage;
                 setError(specificError);
                setIsLoading(false);
            }
        } catch (err) { // Use 'err' without specific type in JS or use unknown with checks
            console.error("Error calling 'newUser' function:", err);
            let specificError = "An unexpected error occurred while creating the account.";
             // Check error structure carefully in JS
             if (err && err.code && err.message) {
                 const code = err.code;
                 const message = err.message;

                 if (code === 'functions/already-exists' || message.includes('EMAIL_EXISTS')) {
                     specificError = "An account with this email address already exists. Try logging in.";
                 } else if (code === 'functions/permission-denied') {
                      specificError = "Permission denied. You might not be allowed to join the linked organization, or the ID might be invalid.";
                 } else if (code === 'functions/not-found') {
                      specificError = `Server error: Could not find the account creation service.`;
                 } else if (code === 'functions/invalid-argument') {
                     specificError = "Invalid data provided to server. Please check your input.";
                 } else {
                     specificError = `Server error: ${message} (code: ${code})`;
                 }
            }
            setError(specificError);
            setIsLoading(false);
        }
    };

    // Determine if form inputs should be disabled
    const formInputsDisabled = isLoading || !!successMessage;

    return (
        <div className="container mt-4">
            <div className="row justify-content-center">
                <div className="col-md-8 col-lg-6">
                    <div className="card shadow-sm">
                        <div className="card-body p-4">
                            {/* Page Title */}
                            <h2 className="card-title text-center mb-3">
                                <FontAwesomeIcon icon={faChurch} className="me-2" />
                                Join Your Church Directory
                            </h2>
                            {/* Introductory text */}
                            <p className="text-center text-muted mb-4">
                                Set up your personal account to access your church directory. Access is granted by using the same email address you provided your church administrator when you were added to the directory.
                            </p>

                            {/* Removed Org Info Box */}

                            {/* Error Display Box */}
                            {error && ( <div className="alert alert-danger d-flex align-items-center" role="alert"> <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 flex-shrink-0" /> <div>{error}</div> </div> )}
                            {/* Success Display Box */}
                            {successMessage && ( <div className="alert alert-success d-flex align-items-center" role="alert"> <FontAwesomeIcon icon={faCheckCircle} className="me-2 flex-shrink-0" /> <div>{successMessage}</div> </div> )}

                            {/* Registration Form */}
                            {!successMessage && (
                                <form onSubmit={handleSubmit} noValidate>
                                    {/* Form Inputs ... (Unchanged) */}
                                    <div className="mb-3"> <label htmlFor="emailInput" className="form-label">Your Email Address</label> <input type="email" className="form-control" id="emailInput" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={formInputsDisabled} /> </div>
                                    <div className="mb-3"> <label htmlFor="nameInput" className="form-label">Your Full Name</label> <input type="text" className="form-control" id="nameInput" value={fullName} onChange={(e) => setFullName(e.target.value)} required disabled={formInputsDisabled} /> </div>
                                    <div className="mb-3"> <label htmlFor="passwordInput" className="form-label">Create Password</label> <input type="password" className="form-control" id="passwordInput" value={password} onChange={(e) => setPassword(e.target.value)} required aria-describedby="passwordHelp" disabled={formInputsDisabled}/> <div id="passwordHelp" className="form-text">Must be at least 6 characters long.</div> </div>
                                    <div className="mb-3"> <label htmlFor="confirmPasswordInput" className="form-label">Confirm Password</label> <input type="password" className="form-control" id="confirmPasswordInput" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required disabled={formInputsDisabled}/> </div>
                                    {/* Submit Button */}
                                    <div className="d-grid"> <button type="submit" className="btn btn-primary" disabled={formInputsDisabled} > {isLoading ? ( <> <FontAwesomeIcon icon={faSpinner} spin className="me-2" /> Creating Account... </> ) : ( "Create Account" )} </button> </div>
                                </form>
                            )}

                            {/* Footer Links */}
                            <hr className="my-4" />
                            <div className="text-center"> <p className="mb-2"> Already have an account? <Link to="/login">Log In Here</Link> </p> <p className="small mt-1 mb-0"> Are you a church administrator looking to set up a *new* directory? <Link to="/register-organization">Register Your Church</Link> </p> </div>
                        </div> {/* End card-body */}
                    </div> {/* End card */}
                </div> {/* End col */}
            </div> {/* End row */}
        </div> // End container
    );
}

export default CreateAccountPage;